'use client';

import * as React from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

// Shadcn chart primitives (`ChartContainer` provides ChartContext — distinct from `ChartFrame`).
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/hen-ui/shadcn/chart';

import { cn, composeRefs } from '@/hen-ui/utils';

import { ChartLegend } from '../ChartLegend';
import {
  getSafeChartProps,
  useMissingChartConfigWarning,
  buildSafeSeriesKeyMap,
  renderReferenceLines,
  resolveTooltipValueFormat,
  renderChartTick,
  deriveValueAxisDefaults,
  validateTooltipMode,
  renderTooltipFooter,
  usePinnedTooltip,
  useInteractiveLegend,
  X_AXIS_LABEL_MARGIN_PX,
  useXAxisConfig,
  sanitizeTicks,
} from '../chart-utils';
import {
  withSeriesColorScaleDefaults,
  resolveDivergingArmColor,
} from '../chart-palette';
import {
  shouldRotateXAxisLabels,
  getDynamicXAxisHeight,
  getXAxisRightMargin,
} from '@/hen-ui/utils/chart';
import type {
  ChartDataPoint,
  ReferenceLineConfig,
  SeriesColorConfig,
  ChartTooltipMode,
  ChartTooltipFooter,
  ColorScale,
  SequentialPalette,
  DivergingPalette,
  XAxisInterval,
} from '../chart-types';
import {
  resolveTickFormat,
  resolveTooltipFormat,
  resolveValueFormat,
  type TickFormat,
  type TooltipFormat,
  type ValueFormat,
} from '../number-format';
import {
  evaluateColorRules,
  resolveSeriesBaseColor,
  buildTooltipIndicatorColorResolver,
  withSemanticLegendColors,
} from '../chart-color-rules';

export interface LineChartProps {
  /** Pre-structured data rows; keys match xAxisKey and the entries in series */
  data: ChartDataPoint[];
  /** Chart config: maps each series key to label + color (shadcn ChartConfig) */
  config: ChartConfig;
  /** Ordered list of series keys to render (one line per key) */
  series: string[];
  /** Key in each data row used for the X-axis category */
  xAxisKey: string;
  /** Label displayed alongside the X-axis */
  xAxisLabel?: string;
  /** Label displayed alongside the Y-axis */
  yAxisLabel?: string;
  /** Fixed Y-axis range, e.g. [60, 95] for percentage charts */
  yAxisDomain?: [number, number];
  /** Specific tick values on the Y-axis, e.g. [60, 70, 80, 90] */
  yAxisTicks?: number[];
  /** Formatter for Y-axis tick labels */
  yAxisFormat?: ValueFormat;
  /**
   * Formatter for the numeric value rendered in the tooltip. Receives the
   * series name so multi-series charts with heterogeneous units can format
   * per-series. Defaults to `yAxisFormat` if not provided, so a percentage
   * Y-axis ("75%") produces a percentage tooltip value automatically.
   */
  tooltipValueFormat?: (value: number, name: string | number) => string;
  /** When true, suppresses the tooltip header label; series rows are unaffected */
  hideLabel?: boolean;
  /**
   * Chart height in px (default 300).
   *
   * - With `fillContainer={false}` (default): fixed at exactly this many pixels.
   * - With `fillContainer={true}`: acts as the **minimum** height. The chart
   *   contributes this value upward to the layout's auto-sizing chain (so the
   *   parent grid row is at least this tall) and stretches beyond it via `h-full`
   *   when a sibling makes the row taller.
   */
  height?: number;
  /**
   * When true, the chart fills its parent container's height. The parent must
   * provide a definite height context (e.g. a flex column where this chart is
   * `flex-1`, or a grid cell). `height` continues to act as the minimum floor.
   */
  fillContainer?: boolean;
  /** Curve interpolation type (default "monotone") */
  curveType?: 'monotone' | 'linear';
  /** Show dots on data points (default true) */
  showDots?: boolean;
  /** Show legend below chart (default true) */
  showLegend?: boolean;
  /**
   * When `true`, each legend item is an interactive toggle button.
   * Clicking (or pressing Enter / Space) hides / shows that series.
   * Cannot hide the last visible series.
   *
   * Has no effect when `showLegend` is `false` — setting both is a
   * misconfiguration and emits a one-time `console.warn`.
   *
   * **Axis stability:** The value-axis domain remains derived from all
   * declared series (not just visible ones) so the Y-axis does not rescale
   * on toggle. This avoids disorienting jumps and preserves reference-line
   * positions.
   *
   * @default false
   */
  clickableLegend?: boolean;
  /** Connect across null/missing values (default false) */
  connectNulls?: boolean;
  /**
   * Optional reference/target lines drawn on top of the chart
   * (e.g. "target acceptance rate: 75%"). See `ReferenceLineConfig`.
   */
  referenceLines?: ReferenceLineConfig[];
  /**
   * Override the default character-count limit (30) used when truncating
   * x-axis labels. Values below 4 are ignored (falls back to the library
   * default). Useful for charts with long category names.
   */
  xAxisLabelMaxLength?: number;
  /**
   * Per-series color configuration for conditional coloring. When
   * `semanticColor` is set without rules, the entire line stroke shifts
   * to the semantic token. When `colorRules` are provided, dots receive
   * per-point conditional fill via a custom render function.
   */
  seriesColorConfigs?: Record<string, SeriesColorConfig>;
  /**
   * Color-scale mode for the per-series default color (default
   * `'categorical'`). `'sequential'` spreads a light→dark gradient across the
   * visible series' line strokes; `'diverging'` colors each line's dots by the
   * data point's sign (the stroke uses the family's high arm). Semantic rules
   * (`seriesColorConfigs`) and explicit `config[key].color` still win. Omitting
   * this renders byte-identically to categorical.
   */
  colorScale?: ColorScale;
  /** Sequential palette family when `colorScale='sequential'` (default `'blues'`). */
  sequentialPalette?: SequentialPalette;
  /** Diverging palette family when `colorScale='diverging'` (default `'blueOrange'`). */
  divergingPalette?: DivergingPalette;
  /**
   * Preferred number of ticks on the X-axis. Recharts uses this as a hint
   * and may adjust to produce "nice" tick values. Only applies to numeric
   * X-axes; ignored for categorical axes (one tick per category).
   *
   * **To use this prop, set `xAxisType="number"`.** Otherwise the axis is
   * categorical and Recharts silently ignores `tickCount` — reach for
   * `xAxisInterval` to thin labels on category axes instead. Setting
   * `xAxisTickCount` on a category axis emits a dev-mode warn telling
   * the operator the prop was ignored (so it doesn't look like the prop
   * is broken).
   *
   * Values outside [2, 50] are clamped with a console warning.
   * When both `xAxisTickCount` and `xAxisInterval` are set on a numeric
   * axis, tickCount wins (interval is dropped — Recharts ignores
   * `interval` on numeric axes anyway). On category axes both props are
   * passed through but Recharts uses only `interval`.
   */
  xAxisTickCount?: number;
  /**
   * Controls which X-axis tick labels are shown. Only applies to
   * categorical X-axes; ignored for numeric axes (use `xAxisTickCount`
   * there). Setting `xAxisInterval` on a numeric axis emits a dev-mode
   * warn telling the operator the prop was ignored.
   *
   * On category axes, when both `xAxisTickCount` and `xAxisInterval` are
   * set, `xAxisInterval` wins (the tickCount value is ignored by Recharts).
   *
   * @see XAxisInterval for the full union (number | 'preserveStart' | …).
   */
  xAxisInterval?: XAxisInterval;
  /**
   * Formatter for X-axis tick labels (not the axis title).
   *
   * @see XAxisTickFormatter for the full rationale (why this is a plain
   * function and not a `MetricFormat` enum) and usage examples.
   */
  xAxisTickFormat?: TickFormat;
  /**
   * When true, shows the X-axis line. When false, hides the axis line
   * while keeping tick marks and labels.
   *
   * @default false (matches existing behavior)
   */
  xAxisLineVisible?: boolean;
  /**
   * Recharts X-axis `type`. Defaults to `'category'`, which is what most
   * LineChart usage wants (string labels per data point). Switch to
   * `'number'` when your `xAxisKey` points to numeric values that should
   * be plotted on a continuous scale — this is the only mode in which
   * `xAxisTickCount` has a visible effect.
   *
   * @default 'category'
   */
  xAxisType?: 'category' | 'number';
  /**
   * Controls when the data-point tooltip is shown.
   * - `'hover'`    — standard hover tooltip (default).
   * - `'pinned'`   — tooltip appears on click and stays until the user
   *                  clicks elsewhere on the chart or presses Escape.
   * - `'disabled'` — tooltip is suppressed entirely.
   */
  tooltipMode?: ChartTooltipMode;
  /**
   * Row-aware value formatter for the tooltip. Receives the numeric value,
   * the series key, and the full raw data row. Takes precedence over
   * `tooltipValueFormat` when both are provided.
   */
  tooltipFormatter?: TooltipFormat;
  /**
   * Methodology copy rendered as a footer note inside the tooltip bubble.
   * - Plain `string`    — rendered verbatim.
   * - `{ md: string }` — parsed for `**bold**`, `*italic*`, `[text](url)`.
   */
  tooltipContent?: ChartTooltipFooter;
}

const EMPTY_FALLBACK_TEXT = 'No data available';

// EDUN-11255 fallback — used only when `deriveValueAxisDefaults` returns
// `undefined` (empty data, single-zero data). See BarChart for full
// rationale; same anchor-at-zero contract for monotonic value axes.
const VALUE_AXIS_DEFAULT_DOMAIN: [number, 'auto'] = [0, 'auto'];

/**
 * LineChart — hen-ui composite wrapping Recharts via the shadcn chart
 * primitive. Stateless, prop-driven, theme-aware via ChartConfig + scoped
 * `--color-<key>` CSS variables.
 */
export const LineChart = React.forwardRef<HTMLDivElement, LineChartProps>(
  (
    {
      data: rawData,
      config,
      series,
      xAxisKey,
      xAxisLabel,
      yAxisLabel,
      yAxisDomain: rawDomain,
      yAxisTicks: rawTicks,
      yAxisFormat: rawYAxisFormat,
      tooltipValueFormat,
      hideLabel,
      height = 300,
      fillContainer = false,
      curveType = 'monotone',
      showDots = true,
      showLegend = true,
      clickableLegend = false,
      connectNulls = false,
      referenceLines,
      xAxisLabelMaxLength,
      seriesColorConfigs,
      colorScale = 'categorical',
      sequentialPalette,
      divergingPalette,
      xAxisTickCount: rawXAxisTickCount,
      xAxisInterval: rawXAxisInterval,
      xAxisTickFormat: rawXAxisTickFormat,
      xAxisLineVisible = false,
      xAxisType = 'category',
      tooltipMode,
      tooltipFormatter: rawTooltipFormatter,
      tooltipContent,
    },
    ref,
  ) => {
    const yAxisFormat = React.useMemo(
      () => resolveValueFormat(rawYAxisFormat),
      [rawYAxisFormat],
    );
    const xAxisTickFormat = React.useMemo(
      () => resolveTickFormat(rawXAxisTickFormat),
      [rawXAxisTickFormat],
    );
    const tooltipFormatter = React.useMemo(
      () => resolveTooltipFormat(rawTooltipFormatter, yAxisFormat),
      [rawTooltipFormatter, yAxisFormat],
    );

    const {
      data,
      yAxisDomain,
      yAxisTicks: rawYAxisTicks,
    } = getSafeChartProps({
      data: rawData,
      yAxisDomain: rawDomain,
      yAxisTicks: rawTicks,
    });

    // EDUN-12034 — drop NaN / non-numbers (with per-entry warn), dedupe, and
    // sort the consumer-supplied tick list. `[]` and all-invalid inputs
    // collapse to `undefined` so the downstream auto-deriver takes over,
    // matching the AC's "empty array → auto-ticks" contract.
    const yAxisTicks = React.useMemo(
      () => sanitizeTicks(rawYAxisTicks, 'LineChart'),
      [rawYAxisTicks],
    );

    // Sanitize series keys into CSS-custom-property-safe tokens. Human-readable
    // column names like "Fall 2026" produce `--color-Fall 2026` — an invalid CSS
    // declaration that the browser silently drops, leaving the series black with
    // an empty legend swatch. `dataKey` on each <Line> keeps the raw key so
    // Recharts reads the correct data column; `name`, config keys, and
    // `var(--color-*)` references all use the safe token throughout.
    const safeKeyMap = React.useMemo(
      () => buildSafeSeriesKeyMap(series),
      [series],
    );
    const safeSeries = React.useMemo(
      () => series.map((k) => safeKeyMap.get(k) ?? k),
      [series, safeKeyMap],
    );
    const safeConfig = React.useMemo<ChartConfig>(() => {
      const next: ChartConfig = {};
      for (const rawKey of series) {
        const safeKey = safeKeyMap.get(rawKey) ?? rawKey;
        const entry = config[rawKey];
        if (entry) {
          next[safeKey] =
            safeKey !== rawKey
              ? { ...entry, label: entry.label ?? rawKey }
              : entry;
        } else if (safeKey !== rawKey) {
          next[safeKey] = { label: rawKey };
        }
      }
      return next;
    }, [series, config, safeKeyMap]);
    const safeSeriesColorConfigs = React.useMemo<
      Record<string, SeriesColorConfig> | undefined
    >(() => {
      if (!seriesColorConfigs) return undefined;
      const next: Record<string, SeriesColorConfig> = {};
      for (const [rawKey, val] of Object.entries(seriesColorConfigs)) {
        next[safeKeyMap.get(rawKey) ?? rawKey] = val;
      }
      return next;
    }, [seriesColorConfigs, safeKeyMap]);

    useMissingChartConfigWarning('LineChart', config, series);

    // EDUN-12033 — validate + resolve the four interlocking X-axis props
    // (`type`, `domain`, `tickCount`, `interval`) in one hook. Emits warnings
    // for clamped / invalid / conflicting input and encapsulates the
    // back-compat rule (`interval={0}` default when caller did not supply
    // one). Returned values are spread directly onto `<XAxis>` below.
    const xAxisConfig = useXAxisConfig({
      rawTickCount: rawXAxisTickCount,
      rawInterval: rawXAxisInterval,
      xAxisType,
      chartName: 'LineChart',
    });

    const { hiddenSeries, handleSeriesToggle } = useInteractiveLegend({
      series: safeSeries,
      showLegend,
      clickableLegend,
      chartName: 'LineChart',
    });

    // Auto-fill missing per-series colors based on `colorScale`. Categorical
    // (default) is byte-identical to historical behavior. Sequential spreads a
    // gradient across the visible series; diverging seeds the high-arm stroke
    // (per-point sign mapping is applied to the dots below). Explicit
    // `config[key].color` overrides still win.
    const resolvedConfig = React.useMemo(
      () =>
        withSeriesColorScaleDefaults(safeConfig, safeSeries, {
          colorScale,
          sequentialPalette,
          divergingPalette,
          hiddenSeries,
        }),
      [
        safeConfig,
        safeSeries,
        colorScale,
        sequentialPalette,
        divergingPalette,
        hiddenSeries,
      ],
    );

    const isDiverging = colorScale === 'diverging';

    // Mirror per-series `semanticColor` overrides into the config the
    // `<ChartLegend>` reads from, so the legend swatch matches the rendered
    // line stroke. Series with only `colorRules` (no semanticColor)
    // intentionally keep the base palette color in the legend.
    const legendConfig = React.useMemo(
      () =>
        withSemanticLegendColors(
          resolvedConfig,
          safeSeriesColorConfigs,
          safeSeries,
        ),
      [resolvedConfig, safeSeriesColorConfigs, safeSeries],
    );

    // EDUN-11255 — auto-derive value-axis domain + ticks from observed
    // values when the consumer has not pinned both. Lines have no stack
    // mode, so the value set is just every numeric cell across all series
    // and rows, plus any reference lines on the value axis.
    const valueAxisDefaults = React.useMemo(() => {
      if (yAxisDomain && yAxisTicks) return undefined;

      const cellValues = data.flatMap((row) =>
        series.map((key) =>
          typeof row[key] === 'number' ? (row[key] as number) : NaN,
        ),
      );
      const referenceHits = (referenceLines ?? [])
        .filter((rl) => (rl.axis ?? 'value') === 'value')
        .map((rl) => Number(rl.at))
        .filter(Number.isFinite);

      return deriveValueAxisDefaults([...cellValues, ...referenceHits]);
    }, [data, series, referenceLines, yAxisDomain, yAxisTicks]);

    // Consumer overrides win in this order: explicit prop > derived > fallback.
    const valueAxisDomain =
      yAxisDomain ?? valueAxisDefaults?.domain ?? VALUE_AXIS_DEFAULT_DOMAIN;
    const valueAxisTicks = yAxisTicks ?? valueAxisDefaults?.ticks;

    // Determine if X-axis labels need rotation to prevent overlap
    const needsRotation = shouldRotateXAxisLabels(data, xAxisKey);

    // Calculate X-axis height dynamically when labels are rotated
    const xAxisHeight = needsRotation
      ? getDynamicXAxisHeight(data, xAxisKey, !!xAxisLabel)
      : xAxisLabel
        ? 50
        : 30;

    // In fillContainer mode the chart contributes `height` upward as min-height
    // (so the row is sized from content, same as fixed-pixel mode) and uses
    // `h-full` to stretch when a sibling makes the row taller. In fixed mode it
    // pins to exactly `height`.
    const sizingStyle: React.CSSProperties = fillContainer
      ? { minHeight: height }
      : { height };
    const sizingClass = fillContainer ? 'h-full' : undefined;

    // Resolve tooltip mode once per render to avoid duplicate warnings and repeated validation.
    const resolvedTooltipMode = validateTooltipMode(tooltipMode);

    // Must be called before the early return (rules of hooks).
    const {
      tooltipActive,
      onChartClick,
      containerRef: pinnedRef,
    } = usePinnedTooltip(resolvedTooltipMode);
    const rootRef = React.useMemo(
      () => composeRefs<HTMLDivElement>(pinnedRef, ref),
      [pinnedRef, ref],
    );

    if (!data.length) {
      // Intentionally no `role` — this is static text, not an image or live
      // region. `aria-label` + text content is enough for AT to announce the
      // state when focus reaches it. Consumers that need a polite live
      // region (e.g. "filter produced no results") can wrap the chart in
      // their own `role='status'` container with richer context.
      return (
        <div
          ref={ref}
          aria-label={EMPTY_FALLBACK_TEXT}
          className={cn(
            'flex items-center justify-center rounded bg-neutral-50 text-neutral-500 text-b4',
            sizingClass,
          )}
          style={sizingStyle}
        >
          {EMPTY_FALLBACK_TEXT}
        </div>
      );
    }

    // `role='figure'` (not `role='img'`): `img` is a leaf role and prunes
    // its subtree from the accessibility tree, which shadows Recharts'
    // `role='application'` SVG surface and its accessibilityLayer output.
    // `figure` groups the chart under an accessible name while keeping the
    // inner keyboard-navigable surface exposed to AT users.
    return (
      <div
        ref={rootRef}
        role='figure'
        aria-label='Line chart'
        className={sizingClass}
        style={sizingStyle}
      >
        <ChartContainer
          config={resolvedConfig}
          className='aspect-auto h-full w-full'
        >
          <RechartsLineChart
            data={data}
            accessibilityLayer
            onClick={onChartClick}
            margin={{
              right: getXAxisRightMargin(data, xAxisKey, needsRotation),
              bottom: !showLegend && xAxisLabel ? X_AXIS_LABEL_MARGIN_PX : 0,
            }}
          >
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis
              dataKey={xAxisKey}
              // `type`, `domain`, `tickCount`, and `interval` come from
              // `useXAxisConfig` so the precedence rules (tickCount wins;
              // `interval={0}` default for back-compat; numeric-axis domain
              // → `['dataMin','dataMax']`) live in one place across charts.
              type={xAxisConfig.type}
              domain={xAxisConfig.domain}
              height={xAxisHeight}
              tickCount={xAxisConfig.tickCount}
              interval={xAxisConfig.interval}
              tick={(p) =>
                renderChartTick({
                  ...p,
                  rotate: needsRotation,
                  textAnchor: needsRotation ? 'end' : 'middle',
                  maxLabelLength: xAxisLabelMaxLength,
                  tickFormatter: xAxisTickFormat,
                })
              }
              label={
                xAxisLabel
                  ? { value: xAxisLabel, position: 'insideBottom', offset: -5 }
                  : undefined
              }
              tickLine={false}
              axisLine={xAxisLineVisible}
            />
            <YAxis
              domain={valueAxisDomain}
              ticks={valueAxisTicks}
              tickFormatter={yAxisFormat}
              label={
                yAxisLabel
                  ? {
                      value: yAxisLabel,
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                    }
                  : undefined
              }
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
            />
            {resolvedTooltipMode !== 'disabled' && (
              <ChartTooltip
                cursor={false}
                active={tooltipActive}
                trigger={resolvedTooltipMode === 'pinned' ? 'click' : undefined}
                content={
                  <ChartTooltipContent
                    indicator='line'
                    hideLabel={hideLabel}
                    valueFormatter={
                      tooltipFormatter
                        ? undefined
                        : resolveTooltipValueFormat(
                            tooltipValueFormat,
                            yAxisFormat,
                          )
                    }
                    rowValueFormatter={tooltipFormatter}
                    footer={renderTooltipFooter(tooltipContent)}
                    getItemIndicatorColor={buildTooltipIndicatorColorResolver(
                      safeSeriesColorConfigs,
                    )}
                  />
                }
              />
            )}
            {showLegend && (
              <ChartLegend
                series={safeSeries}
                config={legendConfig}
                seriesColorConfigs={safeSeriesColorConfigs}
                colorScale={colorScale}
                divergingPalette={divergingPalette}
                clickableLegend={clickableLegend}
                hiddenSeries={hiddenSeries}
                onSeriesToggle={handleSeriesToggle}
                verticalAlign='bottom'
                align='center'
                wrapperStyle={
                  xAxisLabel || needsRotation ? { paddingTop: 12 } : undefined
                }
              />
            )}
            {series
              .map((rawKey, i) => ({ rawKey, safeKey: safeSeries[i] }))
              .filter(({ safeKey }) => !hiddenSeries.has(safeKey))
              .map(({ rawKey, safeKey }) => {
                const colorCfg = safeSeriesColorConfigs?.[safeKey];
                const baseStroke = resolveSeriesBaseColor(
                  safeKey,
                  safeSeriesColorConfigs,
                );
                // Install the per-point custom dot when EITHER:
                //   - actual `colorRules` are defined, OR
                //   - `semanticColor === 'auto-by-value'` — which has no
                //     single representative color, so each dot's stroke
                //     must be resolved per-value via `evaluateColorRules`.
                // With only a STATIC `semanticColor` (or no config), we
                // fall back to Recharts' default dot behavior: hollow
                // ring at rest, filled `activeDot` on hover.
                //
                // Carry `colorCfg` through the gate as a single narrowed
                // local so TypeScript knows it's non-null inside the
                // dot/activeDot closures below (avoids non-null
                // assertions on `colorRules` / `semanticColor`).
                const perPointCfg =
                  colorCfg &&
                  (!!colorCfg.colorRules?.length ||
                    colorCfg.semanticColor === 'auto-by-value')
                    ? colorCfg
                    : null;
                // `diverging` colors each dot by its value's sign, so it needs
                // the per-point dot path even without `seriesColorConfigs`. The
                // arm color becomes the per-point default fed to
                // `evaluateColorRules`, so semantic rules still win on top.
                const needsPerPoint = perPointCfg !== null || isDiverging;
                const resolveDotColor = (val: number | null): string => {
                  const defaultColor = isDiverging
                    ? (resolveDivergingArmColor(val, divergingPalette) ??
                      baseStroke)
                    : baseStroke;
                  // Evaluate the raw `colorCfg` (not the perf-gated
                  // `perPointCfg`) so a static `semanticColor` still wins over
                  // the diverging default when diverging forces the dot path.
                  return evaluateColorRules(
                    colorCfg?.colorRules,
                    val,
                    colorCfg?.semanticColor,
                    defaultColor,
                  );
                };

                const customDot =
                  needsPerPoint && showDots
                    ? (props: Record<string, unknown>) => {
                        const { cx, cy, payload } = props as {
                          cx: number;
                          cy: number;
                          payload: Record<string, unknown>;
                        };
                        const val =
                          typeof payload[rawKey] === 'number'
                            ? (payload[rawKey] as number)
                            : null;
                        const strokeColor = resolveDotColor(val);
                        // Match Recharts' default rest-state dot exactly:
                        // hollow (white fill), `r=3`, with the resolved
                        // per-point color as the border. The `activeDot`
                        // below fills with the same color on hover.
                        // `data-hen-rule-dot` is the test hook — distinguish
                        // these from default dots via the attribute, NOT
                        // via radius (which would create a visible size
                        // mismatch between rule-active and rule-inactive
                        // series in the same chart).
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={3}
                            fill='var(--neutral-white, #FFFFFF)'
                            stroke={strokeColor}
                            strokeWidth={1.5}
                            data-hen-rule-dot=''
                          />
                        );
                      }
                    : undefined;

                // When per-point coloring is active (rules OR
                // `'auto-by-value'`), the hover `activeDot` must also
                // evaluate the rule for that point — otherwise Recharts
                // defaults to filling the dot with the line's `stroke`
                // (the series-level base color), which visually
                // contradicts the on-chart per-point Cell color.
                const customActiveDot = needsPerPoint
                  ? (props: Record<string, unknown>) => {
                      const { cx, cy, payload } = props as {
                        cx: number;
                        cy: number;
                        payload: Record<string, unknown>;
                      };
                      const val =
                        typeof payload[rawKey] === 'number'
                          ? (payload[rawKey] as number)
                          : null;
                      const fill = resolveDotColor(val);
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill={fill}
                          stroke={fill}
                          strokeWidth={0}
                        />
                      );
                    }
                  : undefined;

                return (
                  <Line
                    key={safeKey}
                    type={curveType}
                    dataKey={rawKey}
                    name={safeKey}
                    stroke={baseStroke}
                    strokeWidth={2}
                    dot={customDot ?? showDots}
                    connectNulls={connectNulls}
                    activeDot={
                      (customActiveDot ?? { r: 5 }) as React.ComponentProps<
                        typeof Line
                      >['activeDot']
                    }
                  />
                );
              })}
            {renderReferenceLines(referenceLines, 'vertical')}
          </RechartsLineChart>
        </ChartContainer>
      </div>
    );
  },
);
LineChart.displayName = 'LineChart';
